import { useEffect, useState } from 'react';

import { type AssetDelivery, fetchAssetUrl } from './api.ts';

/**
 * Where the browser fetches an object from, for the next fifteen minutes
 * (US-055, US-056).
 *
 * Two views need this — the player and the gallery — so it lives here rather
 * than in either of them: a second copy would be a second refresh cadence and a
 * second answer to "is this file in the bucket yet?".
 */

/**
 * How long the page waits before asking for fresh URLs.
 *
 * A presigned URL is good for fifteen minutes (US-055), so twelve leaves a
 * margin for a download started just before the swap. Re-resolving never
 * touches the `<video>` element itself — the element is keyed by version, and
 * a `<source>` whose `src` changes does not reload a media element — so a
 * render playing across the boundary keeps playing.
 */
export const ASSET_URL_REFRESH_MS = 12 * 60 * 1000;

/**
 * The delivery of each asset id, refreshed before its URL expires.
 *
 * A listing carries an `assetId` per file, never a URL: a URL in a payload that
 * is polled every three seconds would be a fresh signature every three seconds,
 * and a `src` that changes under a playing video. These are resolved once per
 * set of ids, and again on the timer above.
 *
 * An id that cannot be resolved — nothing uploaded, no bucket configured, the
 * bucket refusing — is simply absent from the map, and the view says so in
 * words rather than rendering a broken element.
 */
export function useAssetUrls(
  contentId: string,
  assetIds: readonly (string | null)[],
): Map<string, AssetDelivery> {
  return useAssetDeliveries(
    assetIds
      .filter((id): id is string => id !== null)
      .map((assetId) => ({ contentId, assetId })),
  );
}

/** One asset of one content: the pair the URL route is keyed by. */
export interface AssetRef {
  readonly contentId: string;
  readonly assetId: string;
}

/**
 * The same thing across contents, for a view whose tiles are not all of one
 * production (US-074).
 *
 * The URL route is scoped to a content — an asset id belonging to another one
 * is not found there — so a thumbnail strip has to carry the pair, not the id.
 * The map is still keyed by asset id alone, because an id is unique across the
 * install and a caller that has the tile has the id.
 */
export function useAssetDeliveries(refs: readonly AssetRef[]): Map<string, AssetDelivery> {
  const [urls, setUrls] = useState<Map<string, AssetDelivery>>(new Map());
  // One string, so the effect re-runs when the *set* of assets changes and not
  // on every render that rebuilt the same array.
  const key = refs.map((ref) => `${ref.contentId}/${ref.assetId}`).join(' ');

  useEffect(() => {
    const wanted = key === '' ? [] : key.split(' ');
    if (wanted.length === 0) {
      setUrls(new Map());
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const resolve = (): void => {
      void Promise.all(
        wanted.map(async (pair): Promise<readonly [string, AssetDelivery] | null> => {
          const slash = pair.indexOf('/');
          const contentId = pair.slice(0, slash);
          const assetId = pair.slice(slash + 1);
          try {
            return [assetId, await fetchAssetUrl(contentId, assetId, controller.signal)] as const;
          } catch {
            return null;
          }
        }),
      ).then((pairs) => {
        if (cancelled) return;
        setUrls(
          new Map(pairs.filter((pair): pair is readonly [string, AssetDelivery] => pair !== null)),
        );
      });
    };
    resolve();
    const timer = window.setInterval(resolve, ASSET_URL_REFRESH_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [key]);

  return urls;
}
